"use client";

import { config } from "@/config/env";

// Types matching backend responses
export type PlanName = "none" | "Small" | "Medium" | "Large";
export type SubscriptionStatus = "active" | "canceled" | "past_due" | "incomplete" | "trialing" | "inactive";

export interface QuotaStatus {
  hasActiveSubscription: boolean;
  planName: PlanName;
  videosUsed: number;
  videosLimit: number;
  videosRemaining: number;
  canCreateVideo: boolean;
  periodStart?: string;
  periodEnd?: string;
}

export interface SubscriptionResponse {
  hasSubscription: boolean;
  planName: string;
  status: string;
  videosPerMonth: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
}

/**
 * Create an authenticated fetcher for subscription API calls
 */
async function authFetch(
  endpoint: string,
  getToken: () => Promise<string | null>
): Promise<Response> {
  const token = await getToken();

  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || `Request failed: ${response.status}`);
  }

  return response;
}

/**
 * Get current quota status for the authenticated user
 */
export async function getQuotaStatus(
  getToken: () => Promise<string | null>
): Promise<QuotaStatus> {
  const response = await authFetch("/api/subscription/quota", getToken);
  return response.json();
}

/**
 * Get subscription details for the authenticated user
 */
export async function getSubscription(
  getToken: () => Promise<string | null>
): Promise<SubscriptionResponse> {
  const response = await authFetch("/api/subscription", getToken);
  return response.json();
}

export interface Plan {
  id: string;
  name: string;
  description: string | null;
  price: number; // Unit amount in cents (SEK)
  currency: string;
  eurPrice: number | null; // Unit amount in cents (EUR), null if not configured
  eurStripePriceId: string | null; // Stripe Price ID for EUR checkout
  videosPerMonth: number;
  stripeProductId: string;
  stripePriceId: string;
  active: boolean;
}

export interface ListPlansResponse {
  plans: Plan[];
}

/**
 * Get all active plans (public endpoint)
 * Called without authentication header as backend endpoint has auth: false
 */
export async function getPlans(acceptLanguage?: string): Promise<ListPlansResponse> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (acceptLanguage) {
        headers["Accept-Language"] = acceptLanguage;
    }

    const response = await fetch(`${config.apiUrl}/plans`, {
        method: "GET",
        headers,
        credentials: "omit", // Public endpoint - no credentials needed
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Request failed" }));
        throw new Error(error.message || `Request failed: ${response.status}`);
    }

    return response.json();
}
